export interface ClimbProps {
  /** Steps reached in the current skill, from the engine (its high-water mark). */
  readonly step: number;
  /** Steps to the mastery gate, from the engine (5 by default). The last one is the summit. */
  readonly steps: number;
  /** What a screen reader hears, e.g. "Fractions: step 3 of 5, taking shape". */
  readonly label: string;
  /** "header" beside the exercise, "card" across a progress card. */
  readonly size?: "header" | "card";
  /** Marks the current stage as just reached (a short pulse, none with reduced motion). */
  readonly fresh?: boolean;
}

const maxSteps = 12;

const box = {
  header: { width: 176, height: 46 },
  card: { width: 300, height: 64 },
} as const;

function whole(value: number, min: number, max: number): number {
  return Number.isFinite(value) ? Math.min(max, Math.max(min, Math.floor(value))) : min;
}

/**
 * Progress inside a skill as a climb (ADR-0043, amending ADR-0040): a trail from the start
 * to a summit flag, with one waypoint per engine step. The trail is solid up to the step
 * reached and dotted beyond it; the current waypoint is larger and ringed. The engine only
 * sends its high-water mark, so the climb never goes back down in front of the child. The
 * app shows the stage in words next to it: the drawing is never the only cue. Numbers are
 * clamped (steps to 1..12, step to 0..steps); NaN shows as the start.
 */
export function Climb(props: ClimbProps) {
  const steps = whole(props.steps, 1, maxSteps);
  const step = whole(props.step, 0, steps);
  const size = props.size ?? "header";
  const { width, height } = box[size];
  const pad = 12;
  const top = 24;
  const points = Array.from({ length: steps + 1 }, (_, i) => ({
    x: pad + (i * (width - pad * 2 - 8)) / steps,
    y: height - pad - (i * (height - pad - top)) / steps,
  }));
  const trail = (from: number, to: number) =>
    points
      .slice(from, to + 1)
      .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(" ");
  const start = points[0] ?? { x: pad, y: height - pad };
  const summit = points[steps] ?? start;
  return (
    <svg
      className="ui-climb"
      data-size={size}
      data-summit={step === steps ? "true" : undefined}
      role="img"
      aria-label={props.label}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      width={width}
      height={height}
    >
      {step < steps && <polyline className="ui-climb__todo" points={trail(step, steps)} />}
      {step > 0 && <polyline className="ui-climb__done" points={trail(0, step)} />}
      <rect
        className="ui-climb__base"
        x={start.x - 7}
        y={start.y + 5}
        width={14}
        height={2}
        rx={1}
      />
      <line
        className="ui-climb__pole"
        x1={summit.x}
        y1={summit.y}
        x2={summit.x}
        y2={summit.y - 19}
      />
      <path
        className="ui-climb__flag"
        d={`M${String(summit.x)} ${String(summit.y - 19)} l11 4 l-11 4 z`}
      />
      {points.map((p, i) => {
        if (i === step) {
          return (
            <g
              key={i}
              className="ui-climb__here"
              data-fresh={props.fresh === true ? "true" : undefined}
            >
              <circle className="ui-climb__halo" cx={p.x} cy={p.y} r={12} />
              <circle className="ui-climb__point" data-state="here" cx={p.x} cy={p.y} r={7.5} />
            </g>
          );
        }
        if (i === 0) return null;
        return (
          <circle
            key={i}
            className="ui-climb__point"
            data-state={i < step ? "done" : "todo"}
            cx={p.x}
            cy={p.y}
            r={5.5}
          />
        );
      })}
    </svg>
  );
}
